import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { api } from "../api";
import usePageTitle from "../hooks/usePageTitle";
import PricingDecisionReview from "./PricingDecisionReview";
import DatabaseQualityPanel from "./DatabaseQualityPanel";

function formatNumber(value, suffix = "") {
  const number = Number(value);
  if (!Number.isFinite(number)) return `0${suffix}`;
  return `${new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(number)}${suffix}`;
}

function formatMs(value) {
  return `${formatNumber(value)} ms`;
}

function formatPercent(value) {
  if (value == null || value === "") return "-";
  const number = Number(value);
  if (!Number.isFinite(number)) return "-";
  return formatNumber(number, "%");
}

function formatConfidence(value) {
  if (value == null || value === "") return "Not enough data";
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return "Not enough data";
  return formatPercent(number);
}

function formatDuration(value) {
  const ms = Number(value);
  if (!Number.isFinite(ms) || ms <= 0) return "0 min";
  const minutes = Math.round(ms / 60000);
  if (minutes < 60) return `${formatNumber(minutes)} min`;
  return `${formatNumber(minutes / 60)} hr`;
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return String(value);
  return date.toLocaleString();
}

function cents(value) {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return "-";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(number / 100);
}

function formatReportDate(value) {
  const date = value ? new Date(value) : new Date();
  if (!Number.isFinite(date.getTime())) return new Date().toISOString().slice(0, 10);
  return date.toISOString().slice(0, 10);
}

function buildLoadedDashboardPayload(data, generatedAt) {
  return {
    generated_at: generatedAt || new Date().toISOString(),
    selected_time_window: data.summary?.selected_window || data.pricing?.selected_window || "last_24h",
    window_label: data.summary?.window_label || data.pricing?.window_label || "Last 24h",
    summary: data.summary || {},
    scanning: data.scanning || {},
    pricing: data.pricing || {},
    scan_stage_timing: data.scanning?.scan_stage_timing || [],
    scan_examples: data.scanning?.scan_examples || {},
    pricing_health: data.pricing?.pricing_health || {},
    pricing_quality: data.pricing?.pricing_quality || {},
    pricing_comp_distribution: data.pricing?.pricing_comp_distribution || { available: false, rows: [] },
    pricing_quality_by_category: data.pricing?.pricing_quality_by_category || [],
    pricing_review_queue: data.pricing?.pricing_review_queue || [],
    pricing_insights: data.pricing?.pricing_insights || { available: false, insights: [] },
    scanner_funnel: data.scanning?.scanner_funnel || {},
    scanner_latency_percentiles: data.scanning?.scanner_latency_percentiles || {},
    fresh_market_metrics: data.pricing?.fresh_market_metrics || {},
    pricing_funnel_metrics: data.pricing?.pricing_funnel_metrics || {},
    pricing_by_category: data.pricing?.pricing_by_category || [],
    carry_forward_age: data.pricing?.carry_forward_age || {},
    catalog_freshness: data.pricing?.catalog_freshness || {},
    trends: {
      pricing: data.pricing?.trends || {},
      scanning: data.scanning?.trends || {},
    },
    sp_special_telemetry: data.pricing?.sp_special_telemetry || {},
    save_method_breakdown: data.pricing?.save_method_breakdown || [],
    top_pricing_opportunities: data.pricing?.top_pricing_opportunities || [],
    pricing_opportunity_examples: data.pricing?.pricing_opportunity_examples || {},
    pricing_true_error_examples: data.pricing?.pricing_true_error_examples || [],
    coverage_guardrails: data.pricing?.coverage_guardrails || {},
  };
}

function formatKeyLabel(value) {
  return String(value || "unknown")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function displayValue(value, formatter = formatNumber) {
  if (value == null) return "Unavailable";
  return formatter(value);
}

function statusChipColor(status) {
  const key = String(status || "").toLowerCase();
  if (key.includes("healthy")) return { bgcolor: "#DCFCE7", color: "#166534" };
  if (key.includes("watch")) return { bgcolor: "#FEF3C7", color: "#92400E" };
  if (key.includes("critical")) return { bgcolor: "#FEE2E2", color: "#991B1B" };
  return { bgcolor: "#E5E7EB", color: "#374151" };
}

function trendFormatterForKey(key) {
  const percentKeys = new Set([
    "fresh_market_coverage",
    "fresh_share_of_saved_prices",
    "carry_forward_dependence",
    "scanner_match_rate",
    "scanner_timeout_rate",
  ]);
  if (percentKeys.has(key)) return formatPercent;
  if (key === "scanner_p90_latency_ms") return formatMs;
  return formatNumber;
}

// eslint-disable-next-line no-unused-vars
function formatTrend(trend, suffix = "") {
  if (!trend || trend.delta == null) return "";
  const arrow = trend.direction === "up" ? "↑" : trend.direction === "down" ? "↓" : "→";
  const delta = Number(trend.delta);
  if (!Number.isFinite(delta) || delta === 0) return `${arrow} flat`;
  return `${arrow} ${delta > 0 ? "+" : ""}${formatNumber(delta, suffix)}`;
}

function appendRows(lines, rows, formatter, emptyLabel = "No data loaded.") {
  if (!Array.isArray(rows) || !rows.length) {
    lines.push(`- ${emptyLabel}`);
    return;
  }
  rows.forEach((row, index) => {
    lines.push(formatter(row, index));
  });
}

function buildIntelligenceSummaryReport(payload) {
  const { generated_at: generatedAt, summary = {}, scanning = {}, pricing = {} } = payload;
  const lines = [
    "TCS Intelligence Center Report",
    `Generated: ${formatDate(generatedAt)}`,
    `Selected time window: ${summary.window_label || payload.window_label || "Last 24h"}`,
    "",
    "Overview",
    `- Scans in selected window: ${formatNumber(summary.window_scan_attempts)}`,
    `- Average scan time in selected window: ${formatMs(summary.window_avg_scan_ms)}`,
    `- Scan success rate in selected window: ${formatNumber(summary.window_scan_success_rate, "%")}`,
    `- All-time root attempts: ${formatNumber(summary.all_time_pricing_attempts)}`,
    `- Root attempts: ${formatNumber(summary.window_pricing_attempts)}`,
    `- Audit rows: ${formatNumber(summary.window_audit_rows ?? pricing.pricing_activity?.audit_rows)}`,
    `- Prices saved: ${formatNumber(summary.window_prices_saved)}`,
    `- True pricing errors: ${formatNumber(summary.window_true_errors)}`,
    `- Informational no-current-market outcomes: ${formatNumber(summary.window_no_comps)}`,
    `- Carry-forward saves: ${formatNumber(summary.window_carry_forward)}`,
    `- Avg pricing confidence: ${formatConfidence(summary.window_avg_confidence_percent)}`,
  ];

  lines.push("", "Health Status");
  const pricingStatus = pricing.health_status || {};
  const scannerStatus = scanning.scanner_health || {};
  lines.push(`- Pricing: ${pricingStatus.status || "Unavailable"}${pricingStatus.reason ? ` (${pricingStatus.reason})` : ""}`);
  lines.push(`- Scanner: ${scannerStatus.status || "Unavailable"}${scannerStatus.reason ? ` (${scannerStatus.reason})` : ""}`);

  const scannerFunnel = scanning.scanner_funnel || {};
  lines.push("", "Scanner Funnel");
  if (scannerFunnel.available === false) {
    lines.push(`- Unavailable: ${scannerFunnel.reason || "Existing scan rows do not expose reliable funnel counts."}`);
  } else {
    const counts = scannerFunnel.counts || {};
    const rates = scannerFunnel.percentages || {};
    [
      ["Total scans", counts.total_scans],
      ["AI extraction completed", counts.ai_extraction_completed],
      ["Strong identifier extracted", `${formatNumber(counts.strong_identifier_extracted)} (${formatPercent(rates.identifier_extraction_rate)})`, "text"],
      ["DB lookup attempted", `${formatNumber(counts.db_lookup_attempted)} (${formatPercent(rates.db_lookup_attempt_rate)})`, "text"],
      ["Candidate found", `${formatNumber(counts.candidate_found)} (${formatPercent(rates.candidate_found_rate)})`, "text"],
      ["Review-only candidate", `${formatNumber(counts.review_only_candidate)} (${formatPercent(rates.review_candidate_rate)})`, "text"],
      ["Successful terminal match", `${formatNumber(counts.successful_terminal_match)} (${formatPercent(rates.successful_match_rate)})`, "text"],
      ["True unknown", `${formatNumber(counts.true_unknown)} (${formatPercent(rates.true_unknown_rate)})`, "text"],
      ["Extraction timeout", `${formatNumber(counts.extraction_timeout)} (${formatPercent(rates.timeout_rate)})`, "text"],
    ].forEach(([label, value, type]) => lines.push(`- ${label}: ${type === "text" ? value : formatNumber(value)}`));
  }

  const latency = scanning.scanner_latency_percentiles || {};
  lines.push("", "Scanner Latency");
  if (latency.available === false) {
    lines.push(`- Unavailable: ${latency.reason || "Latency percentiles unavailable."}`);
  } else {
    [
      ["Total p50", latency.total_scan_p50_ms],
      ["Total p90", latency.total_scan_p90_ms],
      ["Total p95", latency.total_scan_p95_ms],
      ["AI p50", latency.ai_extraction_p50_ms],
      ["AI p90", latency.ai_extraction_p90_ms],
      ["DB p50", latency.db_lookup_p50_ms],
      ["DB p90", latency.db_lookup_p90_ms],
    ].forEach(([label, value]) => lines.push(`- ${label}: ${value == null ? "unavailable" : formatMs(value)}`));
  }

  lines.push("", "Scanning status breakdown");

  appendRows(lines, scanning.status_breakdown, (row) => (
    `- ${formatKeyLabel(row.label || row.status || row.result_status)}: ${formatNumber(row.count)}`
  ));

  lines.push("", "Scanning category performance");
  appendRows(lines, scanning.category_performance, (row) => (
    `- ${formatKeyLabel(row.category)}: ${formatNumber(row.count)} attempts, avg ${formatMs(row.avg_ms)}, avg confidence ${formatNumber(row.avg_confidence)}`
  ));

  lines.push("", "Top scan reject reasons");
  appendRows(lines, scanning.top_reject_reasons, (row) => (
    `- ${formatKeyLabel(row.reason)}: ${formatNumber(row.count)}`
  ), "No scan reject reasons recorded.");

  lines.push("", "Scan stage timing");
  appendRows(lines, scanning.scan_stage_timing, (row) => (
    `- ${row.label || formatKeyLabel(row.stage)}: avg ${formatMs(row.avg_ms)}, max ${formatMs(row.max_ms)}`
  ), "No scan timing captured.");

  lines.push("", "Scan example counts");
  const scanExampleCounts = Object.entries(scanning.scan_examples || {}).map(([group, rows]) => ({
    group,
    count: Array.isArray(rows) ? rows.length : 0,
  }));
  appendRows(lines, scanExampleCounts, (row) => (
    `- ${formatKeyLabel(row.group)}: ${formatNumber(row.count)} examples`
  ), "No scan examples captured.");

  lines.push("", "Pricing status breakdown");
  appendRows(lines, pricing.pricing_outcome_breakdown || pricing.status_breakdown, (row) => (
    `- ${formatKeyLabel(row.label || row.status || row.result_status)}: ${formatNumber(row.count)}`
  ));

  lines.push("", "Pricing health");
  const health = pricing.pricing_health || {};
  [
    ["Attempts", health.attempts],
    ["Successfully priced", health.successfully_priced],
    ["Fresh market", health.fresh_market_prices],
    ["Carry forward", health.carry_forward_prices],
    ["Prior price", health.prior_price_prices],
    ["Manual", health.manual_comp_prices],
    ["Category median", health.category_median_prices],
    ["No current market signals", health.no_current_market],
    ["True errors", health.true_errors],
    ["Average runtime", formatMs(health.avg_runtime_ms), "text"],
  ].forEach(([label, value, type]) => lines.push(`- ${label}: ${type === "text" ? value : formatNumber(value)}`));

  const pricingInsights = pricing.pricing_insights || {};
  lines.push("", "Today's Pricing Insights");
  if (pricingInsights.available === false) {
    lines.push(`- Unavailable: ${pricingInsights.reason || "A complete post-deployment pricing run has not been captured yet."}`);
  } else {
    appendRows(lines, pricingInsights.insights, (insight) => (
      `- [${formatKeyLabel(insight.severity)}] ${insight.summary}${insight.recommendation ? ` Next step: ${insight.recommendation}` : ""}`
    ), "No pricing insights were generated.");
  }

  lines.push("", "Primary Pricing Outcomes");
  appendRows(lines, pricing.primary_outcome_breakdown, (row) => (
    `- ${formatKeyLabel(row.label)}: ${formatNumber(row.count)}`
  ));
  if (pricing.primary_outcome_validation) {
    lines.push(`- Outcome sum equals attempts: ${pricing.primary_outcome_validation.sum_matches_attempts ? "yes" : "no"}`);
  }

  const funnelMetrics = pricing.pricing_funnel_metrics || {};
  lines.push("", "Pricing funnel metrics");
  if (funnelMetrics.available === false) {
    lines.push(`- Unavailable: ${funnelMetrics.reason || "Existing rows do not expose reliable funnel counts."}`);
  } else {
    [
      ["Pricing attempts", funnelMetrics.pricing_attempts],
      ["Browse listings returned", funnelMetrics.browse_listings_returned],
      ["Attempts with accepted current-market comp", funnelMetrics.attempts_with_accepted_current_market_comp ?? funnelMetrics.attempts_with_accepted_exact_comp ?? "unavailable", (funnelMetrics.attempts_with_accepted_current_market_comp ?? funnelMetrics.attempts_with_accepted_exact_comp) == null ? "text" : ""],
      ["Fresh prices calculated", funnelMetrics.fresh_prices_calculated],
      ["Fresh prices saved", funnelMetrics.fresh_prices_saved],
      ["Guardrail-blocked fresh candidates", funnelMetrics.guardrail_blocked_fresh_candidates],
      ["No listings returned", funnelMetrics.no_listings_returned],
      ["Listings returned but all rejected", funnelMetrics.listings_returned_but_all_rejected],
      ["Carry-forward fallback", funnelMetrics.carry_forward_fallback],
      ["Eligible fresh conversion", funnelMetrics.eligible_fresh_conversion_label === "available" ? formatPercent(funnelMetrics.eligible_fresh_conversion_percent) : "unavailable", "text"],
    ].forEach(([label, value, type]) => lines.push(`- ${label}: ${type === "text" ? value : formatNumber(value)}`));
  }

  const freshMarketMetrics = pricing.fresh_market_metrics || {};
  lines.push("", "Fresh market KPI metrics");
  [
    ["Fresh Market Coverage", formatPercent(freshMarketMetrics.fresh_market_coverage_percent), "text"],
    ["Fresh Share of Saved Prices", formatPercent(freshMarketMetrics.fresh_share_of_saves_percent), "text"],
    ["Carry-Forward Dependence", formatPercent(freshMarketMetrics.carry_forward_dependence_percent), "text"],
    ["Additional Fresh Prices Needed for 20%", freshMarketMetrics.fresh_market_gap_to_20],
    ["Additional Fresh Prices Needed for 22%", freshMarketMetrics.fresh_market_gap_to_22],
  ].forEach(([label, value, type]) => lines.push(`- ${label}: ${type === "text" ? value : formatNumber(value)}`));

  const quality = pricing.pricing_quality || {};
  lines.push("", "Pricing Quality");
  if (quality.available === false) {
    lines.push(`- Unavailable: ${quality.reason || "Stored Pricing Evidence is unavailable."}`);
  } else {
    [
      ["Price records", quality.total_price_records],
      ["Fresh Market records", quality.fresh_price_records],
      ["Evidence Coverage", formatPercent(quality.evidence_coverage_percent), "text"],
      ["Average comps used", quality.average_comps_used],
      ["Median comps used", quality.median_comps_used],
      ["Zero-comp Fresh Market prices", quality.zero_comp_fresh_prices],
      ["Single-comp Fresh Market prices", quality.single_comp_fresh_prices],
      ["Low-confidence prices", quality.low_confidence_prices],
      ["Missing confidence", quality.missing_confidence_prices],
      ["Pricing Review Queue", quality.review_queue_count],
    ].forEach(([label, value, type]) => lines.push(`- ${label}: ${type === "text" ? value : formatNumber(value)}`));
  }

  const compDistribution = pricing.pricing_comp_distribution || {};
  lines.push("", "Comparable Depth");
  if (!Array.isArray(compDistribution) && compDistribution.available === false) {
    lines.push(`- Unavailable: ${compDistribution.reason || "Fresh Market classification is unavailable."}`);
  } else {
    appendRows(lines, Array.isArray(compDistribution) ? compDistribution : compDistribution.rows, (row) => `- ${row.label}: ${formatNumber(row.count)}`);
  }

  lines.push("", "Pricing Quality by Category");
  appendRows(lines, (pricing.pricing_quality_by_category || []).slice(0, 10), (row) => (
    `- ${row.category}: ${formatNumber(row.fresh_prices)} Fresh Market prices, evidence ${formatPercent(row.evidence_coverage_percent)}, avg comps ${displayValue(row.average_comps_used)}, avg confidence ${formatConfidence(row.average_confidence_percent)}`
  ), "No Fresh Market category quality data in this window.");

  lines.push("", "Pricing Review Queue Summary");
  lines.push(`- Total review count: ${quality.available === false ? "unavailable" : formatNumber(quality.review_queue_count)}`);
  Object.entries(quality.review_reason_counts || {}).forEach(([reason, count]) => {
    lines.push(`- ${formatKeyLabel(reason)}: ${formatNumber(count)}`);
  });
  appendRows(lines, (pricing.pricing_review_queue || []).slice(0, 10), (row) => (
    `- ${row.item_name || `Item ${row.item_id}`}: ${cents(row.current_price_cents)}, ${formatPercent(row.price_change_percent)} change; ${(row.review_reasons || []).map(formatKeyLabel).join(", ")}`
  ), "No records currently need review.");

  const spTelemetry = pricing.sp_special_telemetry || {};
  lines.push("", "One Piece SP/Special telemetry");
  if (spTelemetry.available === false) {
    lines.push(`- Unavailable: ${spTelemetry.reason || "Existing pricing rows do not expose the reason tag reliably."}`);
  } else {
    [
      ["SP/Special Fresh Saves", spTelemetry.sp_special_fresh_saves],
      ["SP/Special Carry-Forwards", spTelemetry.sp_special_carry_forwards],
      ["SP/Special Not Saved", spTelemetry.sp_special_still_blocked],
    ].forEach(([label, value]) => lines.push(`- ${label}: ${formatNumber(value)}`));
  }

  lines.push("", "Save method breakdown");
  appendRows(lines, pricing.save_method_breakdown, (row) => (
    `- ${formatKeyLabel(row.label)}: ${formatNumber(row.count)}`
  ));

  lines.push("", "Top pricing opportunities");
  appendRows(lines, pricing.top_pricing_opportunities, (row) => (
    `- ${formatKeyLabel(row.reason)}: ${formatNumber(row.count)} ${row.opportunity_type || (row.expected_behavior ? "informational" : "engineering")} ${row.severity || "info"}; ${row.action || ""}`
  ), "No tracked pricing opportunities recorded.");

  lines.push("", "Pricing opportunity example counts");
  const opportunityExamples = pricing.pricing_opportunity_examples || {};
  const opportunityExampleCounts = Object.entries(opportunityExamples).map(([reason, rows]) => ({
    reason,
    count: Array.isArray(rows) ? rows.length : 0,
  }));
  appendRows(lines, opportunityExampleCounts, (row) => (
    `- ${formatKeyLabel(row.reason)}: ${formatNumber(row.count)} examples`
  ), "No opportunity examples captured.");

  lines.push("", "True error examples");
  const trueErrorExamples = Array.isArray(pricing.pricing_true_error_examples) ? pricing.pricing_true_error_examples : [];
  lines.push(`- Captured examples: ${formatNumber(trueErrorExamples.length)}`);

  lines.push("", "Coverage guardrails");
  const guardrails = pricing.coverage_guardrails || {};
  Object.entries(guardrails)
    .filter(([key]) => !key.endsWith("_label"))
    .forEach(([key, value]) => lines.push(`- ${formatKeyLabel(key)}: ${value == null ? "unavailable" : formatNumber(value)}`));

  lines.push("", "Pricing by Category");
  appendRows(lines, (pricing.pricing_by_category || []).slice(0, 10), (row) => (
    `- ${formatKeyLabel(row.category)}: ${formatNumber(row.attempts)} attempts, fresh ${formatPercent(row.fresh_market_coverage_percent)}, carry dependence ${formatPercent(row.carry_forward_dependence_percent)}, true errors ${formatNumber(row.true_errors)}`
  ), "No category pricing data in this window.");

  lines.push("", "Catalog Price Recency");
  const recency = pricing.catalog_freshness || {};
  if (recency.available === false) {
    lines.push(`- Unavailable: ${recency.reason || "Catalog price recency unavailable."}`);
  } else {
    lines.push("- Based on the latest stored price record and may include carry-forward prices. This is not the same as fresh-market coverage.");
    [
      ["Eligible items", recency.total_eligible_catalog_items],
      ["Priced today", recency.priced_today],
      ["Within 7 days", `${formatNumber(recency.priced_within_7_days)} (${formatPercent(recency.percent_priced_within_7_days)})`, "text"],
      ["Within 30 days", `${formatNumber(recency.priced_within_30_days)} (${formatPercent(recency.percent_priced_within_30_days)})`, "text"],
      ["Older than 90 days", recency.older_than_90_days],
      ["Never priced", `${formatNumber(recency.never_priced)} (${formatPercent(recency.percent_never_priced)})`, "text"],
    ].forEach(([label, value, type]) => lines.push(`- ${label}: ${type === "text" ? value : formatNumber(value)}`));
  }

  lines.push("", "Trends");
  const trendRows = [
    ...Object.entries(pricing.trends || {}).map(([key, trend]) => ["Pricing", key, trend]),
    ...Object.entries(scanning.trends || {}).map(([key, trend]) => ["Scanner", key, trend]),
  ];
  appendRows(lines, trendRows, ([group, key, trend]) => {
    const formatter = trendFormatterForKey(key);
    const current = trend?.current == null ? "unavailable" : formatter(trend.current);
    const previous = trend?.previous == null ? "unavailable" : formatter(trend.previous);
    const delta = trend?.delta == null ? "unavailable" : formatter(trend.delta);
    return `- ${group} ${formatKeyLabel(key)}: current ${current}, previous ${previous}, delta ${delta}, ${trend?.direction || "flat"} / ${trend?.interpretation || "neutral"}`;
  }, "No trend data available.");

  lines.push("", "Recent pricing runs");
  appendRows(lines, pricing.recent_pricing_runs, (row) => (
    `- ${row.run_label || row.run_id || "Run"}: ${formatDate(row.started_at)}, ${formatNumber(row.audit_rows)} audit rows, ${formatNumber(row.unique_root_attempts)} root attempts, ${formatNumber(row.unique_items_evaluated)} unique items, ${formatNumber(row.actual_external_searches)} external searches, ${formatNumber(row.terminal_root_attempts ?? row.terminal_decisions)} terminal roots, fresh ${formatNumber(row.fresh_saves)}, carry forward ${formatNumber(row.carry_forward_saves)}, no current market ${formatNumber(row.no_current_market_decisions)}, guardrails ${formatNumber(row.guardrail_blocks)}, retryable ${formatNumber(row.retryable_errors)}, true errors ${formatNumber(row.true_errors)}`
  ), "No pricing runs captured in this window.");

  lines.push("", "Source usage");
  appendRows(lines, pricing.source_usage, (row) => (
    `- ${formatKeyLabel(row.source || row.label)}: ${formatNumber(row.count)}`
  ));

  lines.push("", "Top pricing reject reasons");
  appendRows(lines, pricing.top_reject_reasons, (row) => (
    `- ${formatKeyLabel(row.reason)}: ${formatNumber(row.count)}`
  ), "No pricing reject reasons recorded.");

  return lines.join("\n");
}

async function copyTextToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.setAttribute("readonly", "");
  textArea.style.position = "fixed";
  textArea.style.left = "-9999px";
  document.body.appendChild(textArea);
  textArea.select();
  document.execCommand("copy");
  document.body.removeChild(textArea);
}

const cardSx = {
  p: { xs: 2, md: 2.5 },
  borderRadius: 2,
  border: "1px solid rgba(31,41,55,0.08)",
  boxShadow: "0 14px 34px rgba(15,23,42,0.07)",
};

const TIME_WINDOWS = [
  { value: "last_24h", label: "Last 24h" },
  { value: "last_48h", label: "Last 48h" },
  { value: "last_7d", label: "Last 7d" },
  { value: "today", label: "Today" },
];

function MetricCard({ label, value, sublabel }) {
  return (
    <Paper sx={cardSx}>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography variant="h4" sx={{ mt: 1, fontWeight: 900, color: "#1F2937" }}>
        {value}
      </Typography>
      {sublabel ? (
        <Typography variant="caption" color="text.secondary">
          {sublabel}
        </Typography>
      ) : null}
    </Paper>
  );
}

function EmptyState({ label = "No analytics data yet." }) {
  return (
    <Box sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
      <Typography variant="body2">{label}</Typography>
    </Box>
  );
}

function SectionCard({ title, children }) {
  return (
    <Paper sx={cardSx}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 900 }}>
        {title}
      </Typography>
      {children}
    </Paper>
  );
}

function BarList({ rows, labelKey = "label" }) {
  const max = Math.max(...(rows || []).map((row) => Number(row.count) || 0), 0);
  if (!rows?.length) return <EmptyState />;
  return (
    <Stack spacing={1.5}>
      {rows.map((row) => {
        const count = Number(row.count) || 0;
        const width = max ? `${Math.max(6, (count / max) * 100)}%` : "6%";
        return (
          <Box key={`${row[labelKey]}-${count}`}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
              <Typography variant="body2" sx={{ fontWeight: 800, wordBreak: "break-word" }}>
                {formatKeyLabel(row[labelKey] || row.label || "unknown")}
              </Typography>
              <Chip size="small" label={formatNumber(count)} />
            </Stack>
            <Box sx={{ mt: 0.75, height: 8, bgcolor: "#E6EBF0", borderRadius: 999 }}>
              <Box sx={{ width, height: 8, bgcolor: "#3C6E71", borderRadius: 999 }} />
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}

function PerformanceTable({ rows }) {
  if (!rows?.length) return <EmptyState />;
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small" sx={{ minWidth: 560 }}>
        <TableHead>
          <TableRow>
            <TableCell>Category</TableCell>
            <TableCell align="right">Attempts</TableCell>
            <TableCell align="right">Avg Time</TableCell>
            <TableCell align="right">Avg Confidence</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.category}>
              <TableCell sx={{ fontWeight: 700 }}>{row.category}</TableCell>
              <TableCell align="right">{formatNumber(row.count)}</TableCell>
              <TableCell align="right">{formatMs(row.avg_ms)}</TableCell>
              <TableCell align="right">
                {Object.prototype.hasOwnProperty.call(row, "avg_confidence_percent")
                  ? formatConfidence(row.avg_confidence_percent)
                  : formatNumber(row.avg_confidence)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function RejectReasons({ rows }) {
  if (!rows?.length) return <EmptyState label="No reject reasons recorded." />;
  return <BarList rows={rows.map((row) => ({ label: row.reason, count: row.count }))} />;
}

function SeverityChip({ severity }) {
  const normalized = String(severity || "info").toLowerCase();
  const colors = {
    high: { bgcolor: "#FEE2E2", color: "#991B1B" },
    medium: { bgcolor: "#FEF3C7", color: "#92400E" },
    low: { bgcolor: "#E5E7EB", color: "#374151" },
    info: { bgcolor: "#DCFCE7", color: "#166534" },
  };
  return <Chip size="small" label={normalized} sx={{ fontWeight: 800, ...(colors[normalized] || colors.info) }} />;
}

function PricingHealthCards({ health }) {
  const rows = [
    ["Attempts", health?.attempts, "count"],
    ["Successfully Priced", health?.successfully_priced, "count"],
    ["Fresh Market", health?.fresh_market_prices, "count"],
    ["Carry Forward", health?.carry_forward_prices, "count"],
    ["Prior Price", health?.prior_price_prices, "count"],
    ["Manual", health?.manual_comp_prices, "count"],
    ["Category Median", health?.category_median_prices, "count"],
    ["No Current Market Signals", health?.no_current_market, "count"],
    ["True Errors", health?.true_errors, "count"],
    ["Average Runtime", health?.avg_runtime_ms, "ms"],
  ];
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(5, 1fr)" }, gap: 1.5 }}>
      {rows.map(([label, value, type]) => (
        <MetricCard key={label} label={label} value={type === "ms" ? formatMs(value) : formatNumber(value)} />
      ))}
    </Box>
  );
}

const PRICING_INSIGHT_COLORS = {
  positive: { chipBg: "#DCFCE7", chipColor: "#166534", border: "#22C55E" },
  warning: { chipBg: "#FEF3C7", chipColor: "#92400E", border: "#F59E0B" },
  critical: { chipBg: "#FEE2E2", chipColor: "#991B1B", border: "#EF4444" },
  informational: { chipBg: "#DBEAFE", chipColor: "#1E40AF", border: "#60A5FA" },
};

function PricingInsightsSection({ insights, legacyApproximation = false }) {
  if (insights?.available === false) {
    return (
      <Stack spacing={1}>
        <Alert severity="info">Pricing insights will become available after a complete post-deployment pricing run is captured.</Alert>
        {legacyApproximation ? <Alert severity="warning">Some activity metrics are estimated from historical rows that predate pricing-run telemetry.</Alert> : null}
      </Stack>
    );
  }
  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary">
        Automatically generated from the selected reporting window and latest pricing-run telemetry.
      </Typography>
      {legacyApproximation ? <Alert severity="warning">Some activity metrics are estimated from historical rows that predate pricing-run telemetry.</Alert> : null}
      <Box
        data-testid="pricing-insights-grid"
        sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" }, gap: 1.5 }}
      >
        {(insights?.insights || []).map((insight) => {
          const colors = PRICING_INSIGHT_COLORS[insight.severity] || PRICING_INSIGHT_COLORS.informational;
          const metricAvailable = insight.metric_value != null && insight.metric_value !== "";
          const metricValue = String(insight.metric_label || "").includes("coverage")
            || String(insight.metric_label || "").includes("conversion")
            || String(insight.metric_label || "").includes("dependence")
            ? formatPercent(insight.metric_value)
            : formatNumber(insight.metric_value);
          return (
            <Paper key={insight.id} variant="outlined" sx={{ p: 1.75, borderLeft: `4px solid ${colors.border}`, minWidth: 0 }}>
              <Stack spacing={1}>
                <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} gap={1}>
                  <Chip
                    size="small"
                    label={formatKeyLabel(insight.severity)}
                    sx={{ bgcolor: colors.chipBg, color: colors.chipColor, fontWeight: 900 }}
                  />
                  <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>{insight.title}</Typography>
                </Stack>
                <Typography variant="body2">{insight.summary}</Typography>
                {metricAvailable ? (
                  <Typography variant="body2" sx={{ fontWeight: 900 }}>
                    {metricValue} <Typography component="span" variant="caption" color="text.secondary">{insight.metric_label}</Typography>
                  </Typography>
                ) : null}
                {insight.recommendation ? (
                  <Typography variant="caption" color="text.secondary">
                    <Box component="span" sx={{ fontWeight: 900 }}>Next step:</Box> {insight.recommendation}
                  </Typography>
                ) : null}
              </Stack>
            </Paper>
          );
        })}
      </Box>
    </Stack>
  );
}

function PricingGoals({ pricing }) {
  const latestRun = pricing?.pricing_runs?.[0] || {};
  const activity = pricing?.pricing_activity || {};
  const fresh = Number(pricing?.fresh_market_metrics?.fresh_market_coverage_percent);
  const trueErrors = Number(activity.true_errors ?? pricing?.pricing_health?.true_errors);
  const rootAttempts = Number(latestRun.unique_root_attempts);
  const terminals = Number(latestRun.terminal_root_attempts ?? latestRun.terminal_decisions);
  const terminalCoverage = Number.isFinite(rootAttempts) && rootAttempts > 0 && Number.isFinite(terminals)
    ? (terminals / rootAttempts) * 100
    : null;
  const carry = Number(pricing?.fresh_market_metrics?.carry_forward_dependence_percent);
  const goalRows = [
    {
      label: "Fresh Market Coverage", current: Number.isFinite(fresh) ? fresh : null, target: "30%",
      status: !Number.isFinite(fresh) ? null : fresh >= 30 ? "On Track" : fresh >= 24 ? "Watch" : "Needs Improvement",
    },
    {
      label: "True Errors", current: Number.isFinite(trueErrors) ? trueErrors : null, target: "0",
      status: !Number.isFinite(trueErrors) ? null : trueErrors === 0 ? "On Track" : trueErrors <= 2 ? "Watch" : "Needs Improvement",
      count: true,
    },
    {
      label: "Terminal Decision Coverage", current: terminalCoverage, target: "95%",
      status: terminalCoverage == null ? null : terminalCoverage >= 95 ? "On Track" : terminalCoverage >= 80 ? "Watch" : "Needs Improvement",
    },
    {
      label: "Carry-Forward Dependence", current: Number.isFinite(carry) ? carry : null, target: "Below 50%",
      status: !Number.isFinite(carry) ? null : carry < 50 ? "On Track" : carry <= 60 ? "Watch" : "Needs Improvement",
    },
  ];
  const statusColors = {
    "On Track": { bgcolor: "#DCFCE7", color: "#166534" },
    Watch: { bgcolor: "#FEF3C7", color: "#92400E" },
    "Needs Improvement": { bgcolor: "#FEE2E2", color: "#991B1B" },
  };
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" }, gap: 1 }}>
      {goalRows.map((goal) => (
        <Paper key={goal.label} variant="outlined" sx={{ p: 1.25, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary">{goal.label}</Typography>
          <Typography variant="body2" sx={{ my: 0.5, fontWeight: 900 }}>
            {goal.current == null ? "Awaiting data" : goal.count ? formatNumber(goal.current) : formatPercent(goal.current)}
            <Typography component="span" variant="caption" color="text.secondary"> / target {goal.target}</Typography>
          </Typography>
          <Chip
            size="small"
            label={goal.status || "Awaiting data"}
            sx={{ fontWeight: 800, ...(statusColors[goal.status] || { bgcolor: "#E5E7EB", color: "#374151" }) }}
          />
        </Paper>
      ))}
    </Box>
  );
}

function FreshMarketMetricCards({ metrics }) {
  const rows = [
    ["Fresh Market Coverage", formatPercent(metrics?.fresh_market_coverage_percent)],
    ["Fresh Share of Saved Prices", formatPercent(metrics?.fresh_share_of_saves_percent)],
    ["Carry-Forward Dependence", formatPercent(metrics?.carry_forward_dependence_percent)],
    ["Additional Fresh Prices Needed for 20%", formatNumber(metrics?.fresh_market_gap_to_20)],
    ["Additional Fresh Prices Needed for 22%", formatNumber(metrics?.fresh_market_gap_to_22)],
  ];
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(5, 1fr)" }, gap: 1.5 }}>
      {rows.map(([label, value]) => (
        <MetricCard key={label} label={label} value={value} />
      ))}
    </Box>
  );
}

function PricingQualityCards({ quality }) {
  if (quality?.available === false) {
    return <Alert severity="info">{quality.reason || "Pricing Evidence is unavailable from stored price records."}</Alert>;
  }
  const rows = [
    ["Evidence Coverage", formatPercent(quality?.evidence_coverage_percent)],
    ["Average Comps Used", quality?.average_comps_used == null ? "-" : formatNumber(quality.average_comps_used)],
    ["Median Comps Used", quality?.median_comps_used == null ? "-" : formatNumber(quality.median_comps_used)],
    ["Zero-Comp Fresh Prices", formatNumber(quality?.zero_comp_fresh_prices)],
    ["Single-Comp Fresh Prices", formatNumber(quality?.single_comp_fresh_prices)],
    ["Low-Confidence Prices", formatNumber(quality?.low_confidence_prices)],
    ["Missing Confidence", formatNumber(quality?.missing_confidence_prices)],
    ["Pricing Review Queue", formatNumber(quality?.review_queue_count)],
  ];
  return (
    <Stack spacing={1.5}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }, gap: 1.5 }}>
        {rows.map(([label, value]) => <MetricCard key={label} label={label} value={value} />)}
      </Box>
      <Typography variant="caption" color="text.secondary">
        Evidence coverage measures whether supporting marketplace data was stored with a price. It does not independently verify that the price is correct.
      </Typography>
    </Stack>
  );
}

function ComparableDepth({ distribution }) {
  if (!Array.isArray(distribution) && distribution?.available === false) {
    return <Alert severity="info">{distribution.reason || "Comparable Depth is unavailable because Fresh Market classification is not reliable."}</Alert>;
  }
  return <BarList rows={Array.isArray(distribution) ? distribution : (distribution?.rows || [])} />;
}

function PricingQualityByCategoryTable({ rows, available = true, reason = "" }) {
  if (!available) return <Alert severity="info">{reason || "Pricing quality by category is unavailable."}</Alert>;
  if (!rows?.length) return <EmptyState label="No Fresh Market category quality data in this window." />;
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small" sx={{ minWidth: 900 }}>
        <TableHead>
          <TableRow>
            <TableCell>Category</TableCell>
            <TableCell align="right">Fresh Prices</TableCell>
            <TableCell align="right">Evidence Coverage</TableCell>
            <TableCell align="right">Avg Comps</TableCell>
            <TableCell align="right">Avg Confidence</TableCell>
            <TableCell align="right">Single-Comp</TableCell>
            <TableCell align="right">Low Confidence</TableCell>
            <TableCell align="right">Estimates</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.category}>
              <TableCell sx={{ fontWeight: 700 }}>{row.category}</TableCell>
              <TableCell align="right">{formatNumber(row.fresh_prices)}</TableCell>
              <TableCell align="right">{formatPercent(row.evidence_coverage_percent)}</TableCell>
              <TableCell align="right">{row.average_comps_used == null ? "-" : formatNumber(row.average_comps_used)}</TableCell>
              <TableCell align="right">{formatConfidence(row.average_confidence_percent)}</TableCell>
              <TableCell align="right">{formatNumber(row.single_comp_prices)}</TableCell>
              <TableCell align="right">{formatNumber(row.low_confidence_prices)}</TableCell>
              <TableCell align="right">{formatNumber(row.estimated_prices)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function PricingReviewQueueTable({ rows, available = true, reason = "" }) {
  if (!available) return <Alert severity="info">{reason || "Pricing Review Queue is unavailable."}</Alert>;
  if (!rows?.length) return <EmptyState label="No stored prices need review in this window." />;
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small" sx={{ minWidth: 1250 }}>
        <TableHead>
          <TableRow>
            <TableCell>Item</TableCell>
            <TableCell>Category</TableCell>
            <TableCell align="right">Current Price</TableCell>
            <TableCell align="right">Prior Price</TableCell>
            <TableCell align="right">Change</TableCell>
            <TableCell align="right">Confidence</TableCell>
            <TableCell align="right">Comps</TableCell>
            <TableCell>Source</TableCell>
            <TableCell>Date</TableCell>
            <TableCell>Review Reasons</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.price_id}>
              <TableCell sx={{ minWidth: 180, fontWeight: 700 }}>{row.item_name || `Item ${row.item_id}`}</TableCell>
              <TableCell>{row.category || "Unknown"}</TableCell>
              <TableCell align="right">{cents(row.current_price_cents)}</TableCell>
              <TableCell align="right">{cents(row.prior_price_cents)}</TableCell>
              <TableCell align="right">{formatPercent(row.price_change_percent)}</TableCell>
              <TableCell align="right">{formatConfidence(row.confidence_percent)}</TableCell>
              <TableCell align="right">{row.comps_used == null ? "-" : formatNumber(row.comps_used)}</TableCell>
              <TableCell>{row.source || "-"}</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDate(row.price_date)}</TableCell>
              <TableCell sx={{ minWidth: 260 }}>
                <Stack direction="row" gap={0.5} flexWrap="wrap" useFlexGap>
                  {(row.review_reasons || []).map((reason) => (
                    <Chip key={reason} size="small" label={formatKeyLabel(reason)} />
                  ))}
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function PricingFunnelMetricCards({ metrics }) {
  if (metrics?.available === false) {
    return <Alert severity="info">{metrics.reason || "Pricing funnel metrics are unavailable from existing rows."}</Alert>;
  }
  const rows = [
    ["Pricing Attempts", formatNumber(metrics?.pricing_attempts)],
    ["Browse Listings Returned", formatNumber(metrics?.browse_listings_returned)],
    ["Accepted Current-Market Comp Attempts", (metrics?.attempts_with_accepted_current_market_comp ?? metrics?.attempts_with_accepted_exact_comp) == null ? "unavailable" : formatNumber(metrics?.attempts_with_accepted_current_market_comp ?? metrics?.attempts_with_accepted_exact_comp)],
    ["Fresh Calculated", formatNumber(metrics?.fresh_prices_calculated)],
    ["Fresh Saved", formatNumber(metrics?.fresh_prices_saved)],
    ["Guardrail-Blocked Fresh", formatNumber(metrics?.guardrail_blocked_fresh_candidates)],
    ["No Listings Returned", formatNumber(metrics?.no_listings_returned)],
    ["All Listings Rejected", formatNumber(metrics?.listings_returned_but_all_rejected)],
    ["Carry-Forward Fallback", formatNumber(metrics?.carry_forward_fallback)],
    ["Eligible Fresh Conversion", metrics?.eligible_fresh_conversion_label === "available" ? formatPercent(metrics?.eligible_fresh_conversion_percent) : "unavailable"],
  ];
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(5, 1fr)" }, gap: 1.5 }}>
      {rows.map(([label, value]) => (
        <MetricCard key={label} label={label} value={value} />
      ))}
    </Box>
  );
}

function HealthStatusPanel({ pricingStatus, scannerStatus }) {
  const rows = [
    ["Pricing", pricingStatus],
    ["Scanner", scannerStatus],
  ];
  return (
    <Stack spacing={1.25}>
      <Stack direction={{ xs: "column", sm: "row" }} gap={1.25}>
        {rows.map(([label, status]) => (
          <Box key={label} sx={{ p: 1.5, borderRadius: 2, bgcolor: "#FAFBFC", border: "1px solid rgba(31,41,55,0.1)", flex: 1 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
              <Typography variant="body2" sx={{ fontWeight: 900 }}>{label}</Typography>
              <Chip size="small" label={status?.status || "Unavailable"} sx={{ fontWeight: 800, ...statusChipColor(status?.status) }} />
            </Stack>
            <Typography variant="caption" color="text.secondary">{status?.reason || "No status available."}</Typography>
          </Box>
        ))}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Pricing thresholds: Healthy coverage &gt;= 20% and true errors = 0; Watch coverage 15-19.99% or true errors 1-5; Critical coverage &lt; 15% or true errors &gt; 5. Scanner thresholds: Healthy match rate &gt;= 50% and timeout &lt; 2%; Watch match rate 20-49.99% or timeout 2-5%; Critical match rate &lt; 20% or timeout &gt; 5%.
      </Typography>
    </Stack>
  );
}

function ScannerFunnelCards({ funnel }) {
  if (funnel?.available === false) return <Alert severity="info">{funnel.reason || "Scanner funnel metrics are unavailable from existing rows."}</Alert>;
  const counts = funnel?.counts || {};
  const percentages = funnel?.percentages || {};
  const rows = [
    ["Total Scans", formatNumber(counts.total_scans)],
    ["AI Extraction Completed", formatNumber(counts.ai_extraction_completed)],
    ["Strong Identifier Extracted", formatNumber(counts.strong_identifier_extracted), formatPercent(percentages.identifier_extraction_rate)],
    ["DB Lookup Attempted", formatNumber(counts.db_lookup_attempted), formatPercent(percentages.db_lookup_attempt_rate)],
    ["Candidate Found", formatNumber(counts.candidate_found), formatPercent(percentages.candidate_found_rate)],
    ["Review-Only Candidate", formatNumber(counts.review_only_candidate), formatPercent(percentages.review_candidate_rate)],
    ["Exact Auto-Open Eligible", formatNumber(counts.exact_match_auto_open_eligible)],
    ["Successful Terminal Match", formatNumber(counts.successful_terminal_match), formatPercent(percentages.successful_match_rate)],
    ["True Unknown", formatNumber(counts.true_unknown), formatPercent(percentages.true_unknown_rate)],
    ["Rejected", formatNumber(counts.rejected)],
    ["Extraction Timeout", formatNumber(counts.extraction_timeout), formatPercent(percentages.timeout_rate)],
  ];
  return (
    <Stack spacing={1.5}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }, gap: 1.5 }}>
        {rows.map(([label, value, sublabel]) => (
          <MetricCard key={label} label={label} value={value} sublabel={sublabel} />
        ))}
      </Box>
      <Typography variant="caption" color="text.secondary">
        Low-confidence review candidates are counted separately and are not successful auto-opens.
      </Typography>
    </Stack>
  );
}

function ScannerLatencyTable({ latency }) {
  if (latency?.available === false) return <Alert severity="info">{latency.reason || "Scanner latency percentiles are unavailable."}</Alert>;
  const rows = [
    ["Total Scan p50", latency?.total_scan_p50_ms],
    ["Total Scan p90", latency?.total_scan_p90_ms],
    ["Total Scan p95", latency?.total_scan_p95_ms],
    ["AI Extraction p50", latency?.ai_extraction_p50_ms],
    ["AI Extraction p90", latency?.ai_extraction_p90_ms],
    ["DB Lookup p50", latency?.db_lookup_p50_ms],
    ["DB Lookup p90", latency?.db_lookup_p90_ms],
  ];
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small" sx={{ minWidth: 520 }}>
        <TableBody>
          {rows.map(([label, value]) => (
            <TableRow key={label}>
              <TableCell sx={{ fontWeight: 800 }}>{label}</TableCell>
              <TableCell align="right">{displayValue(value, formatMs)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function PricingByCategoryTable({ rows }) {
  if (!rows?.length) return <EmptyState label="No category pricing data in this window." />;
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small" sx={{ minWidth: 1120 }}>
        <TableHead>
          <TableRow>
            <TableCell>Category</TableCell>
            <TableCell align="right">Attempts</TableCell>
            <TableCell align="right">Fresh Saves</TableCell>
            <TableCell align="right">Carry Saves</TableCell>
            <TableCell align="right">Manual Saves</TableCell>
            <TableCell align="right">No Current Market</TableCell>
            <TableCell align="right">True Errors</TableCell>
            <TableCell align="right">Fresh Coverage</TableCell>
            <TableCell align="right">Fresh Share</TableCell>
            <TableCell align="right">Carry Dependence</TableCell>
            <TableCell align="right">Avg Runtime</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.category}>
              <TableCell sx={{ fontWeight: 800 }}>{row.category}</TableCell>
              <TableCell align="right">{formatNumber(row.attempts)}</TableCell>
              <TableCell align="right">{formatNumber(row.fresh_market_saves)}</TableCell>
              <TableCell align="right">{formatNumber(row.carry_forward_saves)}</TableCell>
              <TableCell align="right">{formatNumber(row.manual_saves)}</TableCell>
              <TableCell align="right">{formatNumber(row.no_current_market_outcomes)}</TableCell>
              <TableCell align="right">{formatNumber(row.true_errors)}</TableCell>
              <TableCell align="right">{formatPercent(row.fresh_market_coverage_percent)}</TableCell>
              <TableCell align="right">{formatPercent(row.fresh_share_of_saved_prices_percent)}</TableCell>
              <TableCell align="right">{formatPercent(row.carry_forward_dependence_percent)}</TableCell>
              <TableCell align="right">{formatMs(row.average_runtime_ms)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function CarryForwardAge({ metrics }) {
  if (metrics?.available === false) return <Alert severity="info">{metrics.reason || "Carry-forward age is unavailable from existing data."}</Alert>;
  return <EmptyState label="No carry-forward age data in this window." />;
}

function CatalogFreshnessCards({ metrics }) {
  if (metrics?.available === false) return <Alert severity="info">{metrics.reason || "Catalog freshness is unavailable from existing data."}</Alert>;
  const rows = [
    ["Eligible Items", formatNumber(metrics?.total_eligible_catalog_items)],
    ["Priced Today", formatNumber(metrics?.priced_today)],
    ["Within 7 Days", formatNumber(metrics?.priced_within_7_days), formatPercent(metrics?.percent_priced_within_7_days)],
    ["Within 30 Days", formatNumber(metrics?.priced_within_30_days), formatPercent(metrics?.percent_priced_within_30_days)],
    ["Within 90 Days", formatNumber(metrics?.priced_within_90_days)],
    ["Older Than 90 Days", formatNumber(metrics?.older_than_90_days)],
    ["Never Priced", formatNumber(metrics?.never_priced), formatPercent(metrics?.percent_never_priced)],
  ];
  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary">
        Based on the latest stored price record and may include carry-forward prices. This is not the same as fresh-market coverage.
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }, gap: 1.5 }}>
        {rows.map(([label, value, sublabel]) => (
          <MetricCard key={label} label={label} value={value} sublabel={sublabel} />
        ))}
      </Box>
    </Stack>
  );
}

function PrimaryOutcomeBreakdown({ rows, note }) {
  return (
    <Stack spacing={1.5}>
      <SaveMethodBreakdown rows={rows || []} />
      {note ? <Alert severity="info">{note}</Alert> : null}
    </Stack>
  );
}

function TrendsTable({ pricingTrends, scannerTrends }) {
  const rows = [
    ...Object.entries(pricingTrends || {}).map(([key, trend]) => ({ group: "Pricing", key, trend })),
    ...Object.entries(scannerTrends || {}).map(([key, trend]) => ({ group: "Scanner", key, trend })),
  ];
  if (!rows.length) return <EmptyState label="No trend data available." />;
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small" sx={{ minWidth: 760 }}>
        <TableHead>
          <TableRow>
            <TableCell>Area</TableCell>
            <TableCell>Metric</TableCell>
            <TableCell align="right">Current</TableCell>
            <TableCell align="right">Previous</TableCell>
            <TableCell align="right">Delta</TableCell>
            <TableCell>Direction</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(({ group, key, trend }) => {
            const formatter = trendFormatterForKey(key);
            return (
              <TableRow key={`${group}-${key}`}>
                <TableCell>{group}</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>{formatKeyLabel(key)}</TableCell>
                <TableCell align="right">{trend?.current == null ? "Unavailable" : formatter(trend.current)}</TableCell>
                <TableCell align="right">{trend?.previous == null ? "Unavailable" : formatter(trend.previous)}</TableCell>
                <TableCell align="right">{trend?.delta == null ? "Unavailable" : formatter(trend.delta)}</TableCell>
                <TableCell>
                  <Chip size="small" label={`${trend?.direction || "flat"}${trend?.interpretation ? ` / ${trend.interpretation}` : ""}`} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

function SpSpecialTelemetryCards({ telemetry }) {
  if (telemetry?.available === false) {
    return <Alert severity="info">{telemetry.reason || "SP/Special telemetry is unavailable from existing pricing rows."}</Alert>;
  }
  const rows = [
    ["SP/Special Fresh Saves", telemetry?.sp_special_fresh_saves],
    ["SP/Special Carry-Forwards", telemetry?.sp_special_carry_forwards],
    ["SP/Special Not Saved", telemetry?.sp_special_still_blocked],
  ];
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: 1.5 }}>
      {rows.map(([label, value]) => (
        <MetricCard key={label} label={label} value={formatNumber(value)} />
      ))}
    </Box>
  );
}

function SaveMethodBreakdown({ rows }) {
  if (!rows?.length) return <EmptyState />;
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }, gap: 1.5 }}>
      {rows.map((row) => (
        <Box key={row.label} sx={{ p: 1.5, borderRadius: 2, bgcolor: "#FAFBFC", border: "1px solid rgba(31,41,55,0.1)" }}>
          <Typography variant="caption" color="text.secondary">{formatKeyLabel(row.label)}</Typography>
          <Typography variant="h5" sx={{ fontWeight: 900 }}>{formatNumber(row.count)}</Typography>
        </Box>
      ))}
    </Box>
  );
}

function PricingOpportunities({ rows }) {
  if (!rows?.length) return <EmptyState label="No tracked pricing opportunities recorded." />;
  return (
    <Stack spacing={1.25}>
      {rows.map((row) => (
        <Box key={row.reason} sx={{ p: 1.5, borderRadius: 2, bgcolor: "#FAFBFC", border: "1px solid rgba(31,41,55,0.1)" }}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={1}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 900 }}>{formatKeyLabel(row.reason)}</Typography>
              <Typography variant="caption" color="text.secondary">{row.action}</Typography>
            </Box>
            <Stack direction="row" gap={1} alignItems="center">
              <Chip size="small" label={row.opportunity_type || (row.expected_behavior ? "Informational" : "Engineering")} />
              <SeverityChip severity={row.severity} />
              <Chip size="small" label={formatNumber(row.count)} />
            </Stack>
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}

const OPPORTUNITY_EXAMPLE_GROUPS = [
  { key: "variant_mismatch", label: "Variant Mismatch" },
  { key: "missing_required_token:set", label: "Missing Required Token:Set" },
  { key: "blocked_weak_browse_support", label: "Blocked Weak Browse Support" },
  { key: "category_median_insufficient_bucket", label: "Category Median Insufficient Bucket" },
];

function compactReason(row) {
  const value = row?.result_reason || row?.save_reason || row?.matched_reason || row?.reject_reasons_json;
  if (!value) return "-";
  const text = typeof value === "string" ? value : JSON.stringify(value);
  return text.length > 120 ? `${text.slice(0, 117)}...` : text;
}

function setNumberLabel(row) {
  const setName = row?.set_name || row?.series_collection || "";
  const number = row?.number ? `#${row.number}` : "";
  return [setName, number].filter(Boolean).join(" ") || "-";
}

function trueErrorMainReason(row) {
  const value = row?.save_reason || row?.result_reason || parseReasonValue(row?.reject_reasons_json)[0] || row?.error || row?.error_message;
  if (!value) return "-";
  const text = typeof value === "string" ? value : JSON.stringify(value);
  return text.length > 90 ? `${text.slice(0, 87)}...` : text;
}

function trueErrorDetail(row) {
  const value = row?.error_message || row?.error || row?.result_reason || row?.reject_reasons_json;
  if (!value) return "-";
  const text = typeof value === "string" ? value : JSON.stringify(value);
  return text.length > 140 ? `${text.slice(0, 137)}...` : text;
}

function TrueErrorExamples({ rows }) {
  if (!rows?.length) return <EmptyState label="No true error examples captured in this window." />;
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small" sx={{ minWidth: 1040 }}>
        <TableHead>
          <TableRow>
            <TableCell>Created</TableCell>
            <TableCell>Item</TableCell>
            <TableCell>Category</TableCell>
            <TableCell>Source</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Main Reason</TableCell>
            <TableCell>Error / Result Reason</TableCell>
            <TableCell align="right">Runtime</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={row.attempt_id || row.id || `${row.created_at}-${index}`}>
              <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDate(row.created_at)}</TableCell>
              <TableCell sx={{ minWidth: 220, maxWidth: 340, whiteSpace: "normal", wordBreak: "break-word", fontWeight: 700 }}>
                {row.item_name || row.name || (row.item_id ? `Item ${row.item_id}` : "-")}
              </TableCell>
              <TableCell>{row.category || "-"}</TableCell>
              <TableCell>{row.pricing_source || row.source || "-"}</TableCell>
              <TableCell><Chip size="small" label={row.result_status || "error"} /></TableCell>
              <TableCell sx={{ minWidth: 180, maxWidth: 280, whiteSpace: "normal", wordBreak: "break-word" }}>
                {trueErrorMainReason(row)}
              </TableCell>
              <TableCell sx={{ minWidth: 240, maxWidth: 420, whiteSpace: "normal", wordBreak: "break-word" }}>
                {trueErrorDetail(row)}
              </TableCell>
              <TableCell align="right">{formatMs(row.runtime_ms)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function OpportunityExamples({ examples }) {
  return (
    <Stack spacing={2}>
      {OPPORTUNITY_EXAMPLE_GROUPS.map((group) => {
        const rows = Array.isArray(examples?.[group.key]) ? examples[group.key] : [];
        return (
          <Box key={group.key}>
            <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 900 }}>
                {group.label}
              </Typography>
              <Chip size="small" label={formatNumber(rows.length)} />
            </Stack>
            {!rows.length ? (
              <EmptyState label="No examples captured in this window." />
            ) : (
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small" sx={{ minWidth: 980 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Created</TableCell>
                      <TableCell>Item</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Set / Number</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Confidence</TableCell>
                      <TableCell>Reason</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, index) => (
                      <TableRow key={row.attempt_id || row.id || `${group.key}-${row.created_at}-${index}`}>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDate(row.created_at)}</TableCell>
                        <TableCell sx={{ minWidth: 220, maxWidth: 320, whiteSpace: "normal", wordBreak: "break-word", fontWeight: 700 }}>
                          {row.item_name || row.name || (row.item_id ? `Item ${row.item_id}` : "-")}
                        </TableCell>
                        <TableCell>{row.category || "-"}</TableCell>
                        <TableCell sx={{ minWidth: 150 }}>{setNumberLabel(row)}</TableCell>
                        <TableCell>{row.pricing_source || row.source || "-"}</TableCell>
                        <TableCell>
                          <Chip size="small" label={row.result_status || "unknown"} />
                        </TableCell>
                        <TableCell align="right">{formatConfidence(row.confidence_score_percent)}</TableCell>
                        <TableCell sx={{ minWidth: 220, maxWidth: 360, whiteSpace: "normal", wordBreak: "break-word" }}>
                          {compactReason(row)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Box>
        );
      })}
    </Stack>
  );
}

function CoverageGuardrails({ guardrails }) {
  const rows = Object.entries(guardrails || {})
    .filter(([key]) => !key.endsWith("_label"))
    .map(([key, value]) => ({ label: key, value }));
  if (!rows.length) return <EmptyState label="No guardrail data in this window." />;
  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary">
        These are expected protections and review signals, not pricing job failures.
      </Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }, gap: 1.5 }}>
        {rows.map((row) => (
          <Box key={row.label} sx={{ p: 1.5, borderRadius: 2, bgcolor: "#FAFBFC", border: "1px solid rgba(31,41,55,0.1)" }}>
            <Typography variant="caption" color="text.secondary">{formatKeyLabel(row.label)}</Typography>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              {row.value == null ? "Unavailable" : formatNumber(row.value)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Stack>
  );
}

function PricingRunsCards({ rows, status }) {
  if (status?.available === false) return <Alert severity="info">{status.reason || "Run grouping unavailable from existing pricing-attempt data."}</Alert>;
  if (!rows?.length) return <EmptyState label="No pricing runs captured in this window." />;
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "repeat(2, 1fr)" }, gap: 1.5 }}>
      {rows.map((row, index) => (
        <Box
          key={`${row.run_id || row.run_label || "run"}-${row.start_time || index}`}
          sx={{ p: 2, borderRadius: 2, bgcolor: "#FAFBFC", border: "1px solid rgba(31,41,55,0.1)" }}
        >
          <Stack direction="row" justifyContent="space-between" gap={2}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 900 }}>
                {row.run_label || row.run_id || `Run ${index + 1}`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatDate(row.start_time || row.started_at)}
              </Typography>
            </Box>
            <Chip size="small" label={formatDuration(row.duration ?? row.duration_ms)} />
          </Stack>
          <Box sx={{ mt: 1.5, display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1 }}>
            {[
              ["Audit Rows", row.audit_rows],
              ["Root Attempts", row.unique_root_attempts],
              ["Unique Items", row.unique_items_evaluated],
              ["External Searches", row.actual_external_searches],
              ["Terminal Roots", row.terminal_root_attempts ?? row.terminal_decisions],
              ["Fresh Saves", row.fresh_saves],
              ["Carry Saves", row.carry_forward_saves],
              ["No Market", row.no_current_market_decisions],
              ["Guardrails", row.guardrail_blocks],
              ["Retryable", row.retryable_errors],
              ["True Errors", row.true_errors],
            ].map(([label, value]) => (
              <Box key={label}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 900 }}>{typeof value === "number" ? formatNumber(value) : value}</Typography>
              </Box>
            ))}
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} gap={1} sx={{ mt: 1.5 }}>
            <Chip size="small" label={`Top source: ${row.top_source || row.top_sources?.[0]?.source || "-"}`} />
            <Chip size="small" label={`Top reject: ${row.top_reject_reason || row.top_reject_reasons?.[0]?.reason || "-"}`} />
          </Stack>
        </Box>
      ))}
    </Box>
  );
}

function ConfidenceSummary({ confidence }) {
  if (!confidence) return <EmptyState label="No confidence data in this window." />;
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(5, 1fr)" }, gap: 1.5 }}>
      {[
        ["Average", formatConfidence(confidence.average_confidence ?? confidence.avg_confidence_percent)],
        ["Median", formatConfidence(confidence.median_confidence ?? confidence.median_confidence_percent)],
        ["High", formatConfidence(confidence.high_confidence_percent)],
        ["Medium", formatConfidence(confidence.medium_confidence_percent)],
        ["Low", formatConfidence(confidence.low_confidence_percent)],
      ].map(([label, value]) => (
        <Box key={label}>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>{value}</Typography>
        </Box>
      ))}
    </Box>
  );
}

function ScanStageTiming({ rows }) {
  if (!rows?.length) return <EmptyState label="No scan timing captured." />;
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(5, 1fr)" }, gap: 1.5 }}>
      {rows.map((row) => (
        <Box key={row.stage || row.label} sx={{ p: 1.5, borderRadius: 2, bgcolor: "#FAFBFC", border: "1px solid rgba(31,41,55,0.1)" }}>
          <Typography variant="caption" color="text.secondary">{row.label || formatKeyLabel(row.stage)}</Typography>
          <Typography variant="body1" sx={{ fontWeight: 900 }}>{formatMs(row.avg_ms)}</Typography>
          <Typography variant="caption" color="text.secondary">Max {formatMs(row.max_ms)}</Typography>
        </Box>
      ))}
    </Box>
  );
}

const SCAN_EXAMPLE_GROUPS = [
  { key: "unknown", label: "Unknown" },
  { key: "low_confidence", label: "Low Confidence" },
  { key: "rejected", label: "Rejected" },
  { key: "insufficient_lego_identity", label: "Insufficient LEGO Identity" },
  { key: "slowest_scans", label: "Slowest Scans" },
];

function parseReasonValue(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.flatMap(parseReasonValue);
  if (typeof value === "object") {
    return Object.keys(value).filter(Boolean);
  }
  const text = String(value).trim();
  if (!text) return [];
  try {
    return parseReasonValue(JSON.parse(text));
  } catch (err) {
    return text.split(/[,\n]/).map((part) => part.trim()).filter(Boolean);
  }
}

function scanMainReason(row) {
  return row?.main_reason || parseReasonValue(row?.reject_reasons_json)[0] || row?.error_message || "-";
}

function scanTimingLabel(row) {
  const parts = [
    ["AI", row?.ai_extract_ms],
    ["DB", row?.db_lookup_ms],
    ["Src", row?.candidate_sourcing_ms],
    ["pHash", row?.phash_ms],
    ["Total", row?.total_ms ?? row?.runtime_ms],
  ].filter(([, value]) => value != null && value !== "");
  return parts.length ? parts.map(([label, value]) => `${label} ${formatMs(value)}`).join(" / ") : "-";
}

function ScanExamples({ examples }) {
  return (
    <Stack spacing={2}>
      {SCAN_EXAMPLE_GROUPS.map((group) => {
        const rows = Array.isArray(examples?.[group.key]) ? examples[group.key] : [];
        return (
          <Box key={group.key}>
            <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 900 }}>{group.label}</Typography>
              <Chip size="small" label={formatNumber(rows.length)} />
            </Stack>
            {!rows.length ? (
              <EmptyState label="No examples captured in this window." />
            ) : (
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small" sx={{ minWidth: 920 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Created</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Predicted Item</TableCell>
                      <TableCell align="right">Confidence</TableCell>
                      <TableCell>Result</TableCell>
                      <TableCell>Main Reason</TableCell>
                      <TableCell>Timing</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, index) => (
                      <TableRow key={row.scan_attempt_id || row.id || `${group.key}-${row.created_at}-${index}`}>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDate(row.created_at)}</TableCell>
                        <TableCell>{row.predicted_category || row.category || "-"}</TableCell>
                        <TableCell sx={{ minWidth: 220, maxWidth: 340, whiteSpace: "normal", wordBreak: "break-word", fontWeight: 700 }}>
                          {row.predicted_name || row.predicted_brand || row.predicted_set_name || row.predicted_number || "-"}
                        </TableCell>
                        <TableCell align="right">{row.confidence_value == null ? "-" : formatNumber(row.confidence_value)}</TableCell>
                        <TableCell><Chip size="small" label={row.result_status || "unknown"} /></TableCell>
                        <TableCell sx={{ minWidth: 180, maxWidth: 320, whiteSpace: "normal", wordBreak: "break-word" }}>{scanMainReason(row)}</TableCell>
                        <TableCell sx={{ minWidth: 220, whiteSpace: "normal" }}>{scanTimingLabel(row)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Box>
        );
      })}
    </Stack>
  );
}

function AttemptsTable({ rows, type }) {
  if (!rows?.length) return <EmptyState />;
  const isPricing = type === "pricing";
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Table size="small" sx={{ minWidth: 760 }}>
        <TableHead>
          <TableRow>
            <TableCell>Created</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>{isPricing ? "Category" : "Predicted Category"}</TableCell>
            <TableCell>{isPricing ? "Source" : "Final Source"}</TableCell>
            <TableCell align="right">Time</TableCell>
            <TableCell align="right">Confidence</TableCell>
            {isPricing ? <TableCell align="right">New Price</TableCell> : null}
            <TableCell>Error</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={row.id || `${row.created_at}-${index}`}>
              <TableCell>{formatDate(row.created_at)}</TableCell>
              <TableCell>
                <Chip size="small" label={row.result_status || "unknown"} />
              </TableCell>
              <TableCell>{isPricing ? row.category || "-" : row.predicted_category || "-"}</TableCell>
              <TableCell>{isPricing ? row.source || "-" : row.final_source || "-"}</TableCell>
              <TableCell align="right">{formatMs(isPricing ? row.runtime_ms : row.total_ms)}</TableCell>
              <TableCell align="right">
                {isPricing
                  ? formatConfidence(row.confidence_score_percent)
                  : formatNumber(row.final_confidence)}
              </TableCell>
              {isPricing ? <TableCell align="right">{cents(row.new_price)}</TableCell> : null}
              <TableCell sx={{ maxWidth: 280, whiteSpace: "normal", wordBreak: "break-word" }}>
                {row.error_message || "-"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

export default function AdminIntelligencePage() {
  usePageTitle("TCS Intelligence Center");
  const [tab, setTab] = useState(0);
  const [data, setData] = useState({ summary: null, scanning: null, pricing: null });
  const [generatedAt, setGeneratedAt] = useState("");
  const [timeWindow, setTimeWindow] = useState("last_24h");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [exportMessage, setExportMessage] = useState("");

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [summaryRes, scanningRes, pricingRes] = await Promise.all([
        api.get("/api/admin/intelligence/summary", { params: { window: timeWindow } }),
        api.get("/api/admin/intelligence/scanning", { params: { window: timeWindow } }),
        api.get("/api/admin/intelligence/pricing", { params: { window: timeWindow } }),
      ]);
      setData({
        summary: summaryRes.data?.summary || {},
        scanning: scanningRes.data?.scanning || {},
        pricing: pricingRes.data?.pricing || {},
      });
      setGeneratedAt(new Date().toISOString());
      setExportMessage("");
    } catch (err) {
      if (err?.response?.status === 403) {
        setError("You do not have permission to view this page.");
      } else {
        setError("Unable to load intelligence data.");
      }
    } finally {
      setLoading(false);
    }
  }, [timeWindow]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const overviewCards = useMemo(
    () => {
      const summary = data.summary || {};
      return [
        ["Scans in Window", formatNumber(summary.window_scan_attempts)],
        ["Avg Scan Time", formatMs(summary.window_avg_scan_ms)],
        ["Scan Success Rate", formatNumber(summary.window_scan_success_rate, "%")],
        ["Pricing Attempts / Root Attempts", formatNumber(summary.window_pricing_attempts)],
        ["Audit Rows", formatNumber(summary.window_audit_rows ?? data.pricing?.pricing_activity?.audit_rows)],
        ["Prices Saved", formatNumber(summary.window_prices_saved)],
        ["True Errors", formatNumber(summary.window_true_errors)],
        ["No Comps", formatNumber(summary.window_no_comps)],
        ["Carry Forward Saves", formatNumber(summary.window_carry_forward)],
        ["Avg Pricing Runtime", formatMs(summary.window_avg_runtime_ms)],
        ["Avg Pricing Confidence", formatConfidence(summary.window_avg_confidence_percent)],
      ];
    },
    [data.summary, data.pricing?.pricing_activity?.audit_rows]
  );

  const loadedDashboardPayload = useMemo(
    () => buildLoadedDashboardPayload(data, generatedAt),
    [data, generatedAt]
  );

  const handleCopySummary = async () => {
    setExportMessage("");
    try {
      await copyTextToClipboard(buildIntelligenceSummaryReport(loadedDashboardPayload));
      setExportMessage("Summary copied.");
    } catch (err) {
      setExportMessage("Unable to copy summary.");
    }
  };

  const handleDownloadJson = () => {
    const reportDate = formatReportDate(loadedDashboardPayload.generated_at);
    const blob = new Blob([JSON.stringify(loadedDashboardPayload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `tcs-intelligence-report-${reportDate}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setExportMessage("JSON downloaded.");
  };

  return (
    <Box sx={{ bgcolor: "#ECEFF3", minHeight: "100vh", py: { xs: 3, md: 5 } }}>
      <Container maxWidth="xl">
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2} sx={{ mb: 3 }}>
          <Box>
            <Typography variant="h3" sx={{ fontWeight: 950, color: "#1F2937" }}>
              TCS Intelligence Center
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.75 }}>
              Platform health from scan and pricing attempts.
            </Typography>
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} gap={1} alignItems={{ xs: "stretch", sm: "center" }}>
            <FormControl size="small" sx={{ minWidth: 140, bgcolor: "#fff" }}>
              <InputLabel id="pricing-window-label">Window</InputLabel>
              <Select
                labelId="pricing-window-label"
                value={timeWindow}
                label="Window"
                onChange={(event) => setTimeWindow(event.target.value)}
              >
                {TIME_WINDOWS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="outlined" onClick={handleCopySummary} disabled={loading || !generatedAt}>
              Copy Summary
            </Button>
            <Button variant="outlined" onClick={handleDownloadJson} disabled={loading || !generatedAt}>
              Download JSON
            </Button>
            <Button variant="outlined" onClick={() => loadDashboard()} disabled={loading}>
              Refresh
            </Button>
          </Stack>
        </Stack>

        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        {exportMessage ? <Alert severity={exportMessage.includes("Unable") ? "warning" : "success"} sx={{ mb: 2 }}>{exportMessage}</Alert> : null}
        {loading ? (
          <Stack alignItems="center" sx={{ py: 8 }}>
            <CircularProgress />
          </Stack>
        ) : (
          <>
            <Paper sx={{ ...cardSx, p: 1, mb: 3 }}>
              <Tabs
                value={tab}
                onChange={(_event, next) => setTab(next)}
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab label="Overview" />
                <Tab label="Scanning" />
                <Tab label="Pricing" />
                <Tab label="Pricing Decision Review" />
                <Tab label="Database Quality" />
              </Tabs>
            </Paper>

            {tab === 0 ? (
              <Stack spacing={2}>
                <SectionCard title="Health Status">
                  <HealthStatusPanel pricingStatus={data.pricing?.health_status} scannerStatus={data.scanning?.scanner_health} />
                </SectionCard>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
                    gap: 2,
                  }}
                >
                  {overviewCards.map(([label, value, sublabel]) => (
                    <MetricCard key={label} label={label} value={value} sublabel={sublabel} />
                  ))}
                </Box>
                <SectionCard title="Trends">
                  <TrendsTable pricingTrends={data.pricing?.trends || {}} scannerTrends={data.scanning?.trends || {}} />
                </SectionCard>
              </Stack>
            ) : null}

            {tab === 1 ? (
              <Stack spacing={2}>
                <SectionCard title="Scanner Funnel">
                  <ScannerFunnelCards funnel={data.scanning?.scanner_funnel || {}} />
                </SectionCard>
                <SectionCard title="Scanner Latency">
                  <ScannerLatencyTable latency={data.scanning?.scanner_latency_percentiles || {}} />
                </SectionCard>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "0.8fr 1.2fr" }, gap: 2 }}>
                  <SectionCard title="Status Breakdown">
                    <BarList rows={data.scanning?.status_breakdown || []} />
                  </SectionCard>
                  <SectionCard title="Category Performance">
                    <PerformanceTable rows={data.scanning?.category_performance || []} />
                  </SectionCard>
                </Box>
                <SectionCard title="Top Reject Reasons">
                  <RejectReasons rows={data.scanning?.top_reject_reasons || []} />
                </SectionCard>
                <SectionCard title="Scan Stage Timing">
                  <ScanStageTiming rows={data.scanning?.scan_stage_timing || []} />
                </SectionCard>
                <SectionCard title="Scan Examples">
                  <ScanExamples examples={data.scanning?.scan_examples || {}} />
                </SectionCard>
                <SectionCard title="Slowest Scans">
                  <AttemptsTable rows={data.scanning?.slowest_scans || []} type="scan" />
                </SectionCard>
                <SectionCard title="Recent Scans">
                  <AttemptsTable rows={data.scanning?.recent_scans || []} type="scan" />
                </SectionCard>
              </Stack>
            ) : null}

            {tab === 2 ? (
              <Stack spacing={2}>
                {data.pricing?.primary_outcome_validation?.available === false
                  || data.pricing?.primary_outcome_validation?.sum_matches_attempts === false ? (
                  <Alert severity="warning">Canonical root outcome validation failed. Coverage and outcome metrics are unavailable until telemetry can be reconciled.</Alert>
                ) : null}
                <SectionCard title="Pricing Health">
                  <PricingHealthCards health={data.pricing?.pricing_health || {}} />
                </SectionCard>
                <SectionCard title="Today's Pricing Insights">
                  <PricingInsightsSection
                    insights={data.pricing?.pricing_insights || { available: false, insights: [] }}
                    legacyApproximation={Boolean(data.pricing?.pricing_activity?.legacy_approximation)}
                  />
                </SectionCard>
                <SectionCard title="Pricing Goals">
                  <PricingGoals pricing={data.pricing || {}} />
                </SectionCard>
                <SectionCard title="Fresh Market KPI">
                  <FreshMarketMetricCards metrics={data.pricing?.fresh_market_metrics || {}} />
                </SectionCard>
                <SectionCard title="Pricing Quality">
                  <PricingQualityCards quality={data.pricing?.pricing_quality || {}} />
                </SectionCard>
                <SectionCard title="Comparable Depth">
                  <ComparableDepth distribution={data.pricing?.pricing_comp_distribution || { available: false, rows: [] }} />
                </SectionCard>
                <SectionCard title="Pricing Quality by Category">
                  <PricingQualityByCategoryTable
                    rows={data.pricing?.pricing_quality_by_category || []}
                    available={data.pricing?.pricing_quality?.available !== false}
                    reason={data.pricing?.pricing_quality?.reason}
                  />
                </SectionCard>
                <SectionCard title="Pricing Review Queue">
                  <PricingReviewQueueTable
                    rows={data.pricing?.pricing_review_queue || []}
                    available={data.pricing?.pricing_quality?.available !== false}
                    reason={data.pricing?.pricing_quality?.reason}
                  />
                </SectionCard>
                <SectionCard title="Pricing Funnel">
                  <PricingFunnelMetricCards metrics={data.pricing?.pricing_funnel_metrics || {}} />
                </SectionCard>
                <SectionCard title="Primary Outcomes">
                  <PrimaryOutcomeBreakdown rows={data.pricing?.primary_outcome_breakdown || []} note={data.pricing?.diagnostic_signals_note} />
                </SectionCard>
                <SectionCard title="Pricing by Category">
                  <PricingByCategoryTable rows={data.pricing?.pricing_by_category || []} />
                </SectionCard>
                <SectionCard title="Carry-Forward Age">
                  <CarryForwardAge metrics={data.pricing?.carry_forward_age || {}} />
                </SectionCard>
                <SectionCard title="Catalog Price Recency">
                  <CatalogFreshnessCards metrics={data.pricing?.catalog_freshness || {}} />
                </SectionCard>
                <SectionCard title="Trends">
                  <TrendsTable pricingTrends={data.pricing?.trends || {}} scannerTrends={data.scanning?.trends || {}} />
                </SectionCard>
                <SectionCard title="One Piece SP/Special Telemetry">
                  <SpSpecialTelemetryCards telemetry={data.pricing?.sp_special_telemetry || {}} />
                </SectionCard>
                <SectionCard title="True Error Examples">
                  <TrueErrorExamples rows={data.pricing?.pricing_true_error_examples || []} />
                </SectionCard>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 2 }}>
                  <SectionCard title="Save Method Breakdown">
                    <SaveMethodBreakdown rows={data.pricing?.save_method_breakdown || []} />
                  </SectionCard>
                  <SectionCard title="Coverage Guardrails">
                    <CoverageGuardrails guardrails={data.pricing?.coverage_guardrails || {}} />
                  </SectionCard>
                </Box>
                <SectionCard title="Top Pricing Opportunities">
                  <PricingOpportunities rows={data.pricing?.top_pricing_opportunities || []} />
                </SectionCard>
                <SectionCard title="Opportunity Examples">
                  <OpportunityExamples examples={data.pricing?.pricing_opportunity_examples || {}} />
                </SectionCard>
                <SectionCard title="Recent Pricing Runs">
                  <PricingRunsCards rows={data.pricing?.recent_pricing_runs || []} status={data.pricing?.recent_pricing_runs_status} />
                </SectionCard>
                <SectionCard title="Pricing Confidence">
                  <ConfidenceSummary confidence={data.pricing?.confidence} />
                </SectionCard>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "0.8fr 1.2fr" }, gap: 2 }}>
                  <SectionCard title="Status Breakdown">
                    <BarList rows={data.pricing?.pricing_outcome_breakdown || data.pricing?.status_breakdown || []} />
                  </SectionCard>
                  <SectionCard title="Category Performance">
                    <PerformanceTable rows={data.pricing?.category_performance || []} />
                  </SectionCard>
                </Box>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 2 }}>
                  <SectionCard title="Source Usage">
                    <BarList rows={data.pricing?.source_usage || []} labelKey="source" />
                  </SectionCard>
                  <SectionCard title="Top Reject Reasons">
                    <RejectReasons rows={data.pricing?.top_reject_reasons || []} />
                  </SectionCard>
                </Box>
                <SectionCard title="Slowest Pricing Attempts">
                  <AttemptsTable rows={data.pricing?.slowest_pricing_attempts || []} type="pricing" />
                </SectionCard>
                <SectionCard title="Recent Pricing Attempts">
                  <AttemptsTable rows={data.pricing?.recent_pricing_attempts || []} type="pricing" />
                </SectionCard>
              </Stack>
            ) : null}

            {tab === 4 ? <DatabaseQualityPanel /> : null}
            {tab === 3 ? <PricingDecisionReview window={timeWindow} /> : null}
          </>
        )}
      </Container>
    </Box>
  );
}
